import {
  type ConversationWire,
  listAllConversations,
  listMembershipDelta,
} from '../network/conversations';
import { isRegisteredCallGroup } from '../calls/callGroupRegistry';
import { logger } from '../log/log';
import type { ConversationsPersister, ConversationRecord } from './persistenceAdapters';

/**
 * Populate the local conversation cache from `GET /v1/conversations`.
 *
 * Called from each platform's session bootstrap after `bootstrapSession`
 * succeeds, so a freshly-signed-in device — or a device whose local cache
 * was wiped — sees its existing conversations on the chat list immediately
 * instead of having to wait for a stray Welcome envelope or a manual
 * "new chat" to repopulate them.
 *
 * Encoding contract: the BE returns `conversation_id` as base64url (32-byte
 * id, no padding). The local persistence layer keys conversations by the
 * **hex** of those same bytes (`packages/ui/src/sync/syncEngine.ts:145` —
 * the SDK delivers `Uint8Array` and we `bytesToHex` it). This function
 * decodes wire ids to bytes and re-encodes to hex so rows merge cleanly
 * with whatever the SDK catchup path wrote.
 *
 * Merge semantics:
 *   * `put` is invoked for rows the local store doesn't have yet — those
 *     are pure inserts.
 *   * For rows the store already has, we `update()` only the fields the
 *     server is authoritative for (`createdAt`, `lastMessageAt`) — we do
 *     NOT touch `name`, `peerAccountId`, or `joinedViaInvite`, which are
 *     populated by the Welcome / catchup path and may be richer than what
 *     the server projection carries.
 *
 * The function is idempotent: re-running it produces no row churn beyond
 * `lastMessageAt` updates for conversations whose freshest event advanced.
 */
export interface BootstrapConversationsResult {
  /** Rows inserted (didn't previously exist locally). */
  inserted: number;
  /** Rows whose `lastMessageAt` was bumped forward. */
  updated: number;
  /** Total rows the server returned (full: whole list; delta: change count). */
  serverCount: number;
  /** Hex ids the server returned this call. FULL: EVERY membership (the
   *  authoritative set — callers feed it to the transport + subscribe). DELTA:
   *  only the conversations JOINED since `since` (the new ones to subscribe). */
  conversationIdHexes: string[];
  /** The device's membership version after this call — store it as the cursor
   *  for the next `?since=` delta. `undefined` on a legacy backend (stay full). */
  currentVersion: number | undefined;
  /** True when this was a DELTA (`?since=`) call. */
  isDelta: boolean;
  /** FULL only: false when the page walk was truncated (don't store the version).
   *  Always true for a delta (it paginates to exhaustion). */
  complete: boolean;
  /** DELTA only: the backend's version came back BELOW the `since` we passed —
   *  its membership sequence went backwards, i.e. the backend was reset/rebuilt.
   *  The caller must discard its stored version and full-resync. */
  resetDetected: boolean;
  /** DELTA only: hex ids the server reported as REMOVED (device left/was removed)
   *  — marked read-only locally via `leftAt` (the row STAYS in the list so a
   *  re-invite resumes it with full history); the caller must NOT subscribe
   *  these while the user is not a member. */
  removedIdHexes: string[];
}

export async function bootstrapConversationsFromServer(
  persister: ConversationsPersister,
  opts: { signal?: AbortSignal; pageSize?: number; maxPages?: number; since?: number } = {},
): Promise<BootstrapConversationsResult> {
  // DELTA path: fetch only the memberships added since `opts.since`.
  if (opts.since !== undefined) {
    const deltaOpts: { signal?: AbortSignal } = {};
    if (opts.signal !== undefined) deltaOpts.signal = opts.signal;
    const { changes, currentVersion } = await listMembershipDelta(opts.since, deltaOpts);
    const counters = { inserted: 0, updated: 0 };
    const conversationIdHexes: string[] = [];
    const removedIdHexes: string[] = [];
    for (const w of changes) {
      if (w.op === 'removed') {
        const idHex = base64UrlToHex(w.conversation_id);
        if (idHex === null) {
          logger.warn('bootstrapConversations.bad_id', { prefix: w.conversation_id.slice(0, 8) });
          continue;
        }
        // Server-authoritative departure → mark read-only (leftAt), keeping the
        // row visible so a re-invite resumes it with full history. The update
        // no-ops if the row was never materialized locally, which is fine.
        try {
          await persister.update(idHex, { leftAt: Date.now() });
        } catch {
          /* best-effort */
        }
        removedIdHexes.push(idHex);
        continue;
      }
      const id = await applyWireRow(persister, w, counters);
      if (id !== null) conversationIdHexes.push(id);
    }
    const resetDetected = currentVersion !== undefined && currentVersion < opts.since;
    logger.info('bootstrapConversations.delta.done', {
      since: opts.since,
      changes: changes.length,
      inserted: counters.inserted,
      removed: removedIdHexes.length,
      currentVersion,
      resetDetected,
    });
    return {
      inserted: counters.inserted,
      updated: counters.updated,
      serverCount: changes.length,
      conversationIdHexes,
      currentVersion,
      isDelta: true,
      complete: true,
      resetDetected,
      removedIdHexes,
    };
  }

  // FULL path (cold start / resync): walk every page.
  const callOpts: { pageSize?: number; maxPages?: number; signal?: AbortSignal } = {};
  if (opts.pageSize !== undefined) callOpts.pageSize = opts.pageSize;
  if (opts.maxPages !== undefined) callOpts.maxPages = opts.maxPages;
  if (opts.signal !== undefined) callOpts.signal = opts.signal;
  const { conversations: wire, currentVersion, complete } = await listAllConversations(callOpts);
  const counters = { inserted: 0, updated: 0 };
  const conversationIdHexes: string[] = [];
  for (const w of wire) {
    const id = await applyWireRow(persister, w, counters);
    if (id !== null) conversationIdHexes.push(id);
  }

  logger.info('bootstrapConversations.done', {
    serverCount: wire.length,
    inserted: counters.inserted,
    updated: counters.updated,
    currentVersion,
    complete,
  });

  return {
    inserted: counters.inserted,
    updated: counters.updated,
    serverCount: wire.length,
    conversationIdHexes,
    currentVersion,
    isDelta: false,
    complete,
    resetDetected: false,
    removedIdHexes: [],
  };
}

/**
 * Insert-if-absent (or bump `lastMessageAt`) for one server conversation row.
 * Shared by the full-snapshot and delta paths. Returns the hex id, or `null` for
 * an undecodable id. Mutates `counters` in place.
 */
async function applyWireRow(
  persister: ConversationsPersister,
  w: ConversationWire,
  counters: { inserted: number; updated: number },
): Promise<string | null> {
  const id = base64UrlToHex(w.conversation_id);
  if (id === null) {
    logger.warn('bootstrapConversations.bad_id', { prefix: w.conversation_id.slice(0, 8) });
    return null;
  }
  // Never materialise a per-call MLS group as a chat row. If the server ever
  // lists a call group and it's already known (durable registry), skip it —
  // call infrastructure is owned by the calls subsystem, not the chat list.
  if (isRegisteredCallGroup(id)) {
    return id;
  }
  const existing = await persister.get(id);
  if (existing === undefined) {
    const record: ConversationRecord = {
      id,
      kind: mapKind(w.kind),
      // Display name is filled in by the Welcome handler / peer profile lookup.
      // Empty for now so the chat-list adapter renders a placeholder until the
      // resolver runs.
      name: '',
      createdAt: parseRfc3339(w.created_at) ?? Date.now(),
      lastMessageAt: parseRfc3339(w.last_event_at) ?? undefined,
    } as ConversationRecord;
    if (record.lastMessageAt === undefined) delete (record as { lastMessageAt?: number }).lastMessageAt;
    await persister.put(record);
    counters.inserted++;
  } else {
    const serverLast = parseRfc3339(w.last_event_at);
    const patch: Partial<ConversationRecord> = {};
    if (serverLast !== null && (existing.lastMessageAt ?? 0) < serverLast) {
      patch.lastMessageAt = serverLast;
    }
    // SERVER-AUTHORITATIVE KIND: the server's `messaging.conversations.kind` is
    // the source of truth for direct-vs-group. If it says GROUP but the local row
    // is still a 1:1 (a single-peer group first seen before its MLS meta was
    // readable — the "group + 1:1 share the same avatar" bug), correct it here:
    // mark it a durable group and drop the stale peer id so member-profile
    // hydration stops stamping the peer's avatar onto the group. This heals a
    // mis-classified row on every reconcile without needing a re-upload.
    if (mapKind(w.kind) === 'group' && existing.kind !== 'group') {
      patch.kind = 'group';
      patch.explicitGroup = true;
      if (existing.peerAccountId !== undefined && existing.peerAccountId.length > 0) {
        patch.peerAccountId = '';
      }
    }
    // REVIVE: the server is listing this conversation, so it is NOT orphaned —
    // clear any stale orphan marker from a previous transient 404 streak.
    if ((existing.orphanedAt ?? 0) > 0) {
      patch.orphanedAt = 0;
    }
    if (Object.keys(patch).length > 0) {
      await persister.update(id, patch);
      counters.updated++;
    }
  }
  return id;
}

function mapKind(beKind: ConversationWire['kind']): ConversationRecord['kind'] {
  // The wire carries the RAW server kind (`messaging.conversations.kind`):
  // 'direct_message' | 'group_chat' | 'device_group' | 'space' | 'call_group' |
  // 'workspace_policy' (see services/api conversations/validate.go `validKinds`).
  // ONLY 'direct_message' is a 1:1; every other kind is a group-shaped
  // conversation locally. (Also accept a bare 'direct' defensively in case a
  // caller/legacy path passes the short form.) NOTE: mapping 'direct_message' to
  // 'group' — the previous `=== 'direct'` test — would misclassify EVERY 1:1 the
  // server lists as a group; the applyWireRow kind-heal above relies on this
  // being correct.
  return beKind === 'direct_message' || beKind === 'direct' ? 'direct' : 'group';
}

function parseRfc3339(s: string | undefined): number | null {
  if (!s) return null;
  const n = Date.parse(s);
  return Number.isFinite(n) ? n : null;
}

function base64UrlToHex(s: string): string | null {
  try {
    const padded = s.replace(/-/g, '+').replace(/_/g, '/');
    const fill = (4 - (padded.length % 4)) % 4;
    const b64 = padded + '='.repeat(fill);
    const B = (globalThis as { Buffer?: { from(s: string, enc: string): Uint8Array } }).Buffer;
    let bytes: Uint8Array;
    if (B !== undefined) {
      const buf = B.from(b64, 'base64');
      bytes = new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    } else {
      const bin = atob(b64);
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    }
    let hex = '';
    for (let i = 0; i < bytes.length; i++) hex += bytes[i]!.toString(16).padStart(2, '0');
    return hex;
  } catch {
    return null;
  }
}
